import { OrderBodyNoEditableV2 } from '@portal/shared/ui/OrderBodyNoEditableV2';
import { SubscriptionLineItemV2 } from '@portal/state/internal/getSubscriptionDetails/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HIDDEN_METAFIELD } from 'shared/constants/metafields';
import { pluralize } from 'shared/utils/pluralize';
import { useMedia } from 'state/context/MediaContext';
import { useLanguageValue } from 'state/hooks/useLanguage';
import style from './LineItemsViewV2.module.scss';

interface LineItemsViewPropsV2 {
  lineItems: SubscriptionLineItemV2[];
  currencyCode?: string;
}

export const LineItemsViewV2 = ({ lineItems, currencyCode }: LineItemsViewPropsV2) => {
  const { t } = useTranslation('commonContent');
  const { nextOnlyLabel, outOfStockLabel, productHiddenLabel, quantityLabel } = t(
    'orderBodyContent',
    {
      returnObjects: true
    }
  );
  const { atSmall, atMedium, atLarge, atXXLarge } = useMedia();

  const filteredLineItems = useMemo(
    () =>
      lineItems
        .filter(
          (item) =>
            !item.product?.metafields?.some(
              (metafield) =>
                metafield.key === HIDDEN_METAFIELD.key && metafield.value === HIDDEN_METAFIELD.value
            )
        )
        .sort((a, b) => b.total_amount - a.total_amount),
    [lineItems]
  );

  const createLineItemsList = (list: SubscriptionLineItemV2[]) =>
    list.map((item) => (
      <OrderBodyNoEditableV2
        key={item.id}
        item={item}
        label={item?.is_one_time ? nextOnlyLabel : undefined}
        size={{ s: 'large', xxs: 'small' }}
        outOfStockLabel={outOfStockLabel}
        quantityLabel={quantityLabel}
        hideSubtitle
        currencyCode={currencyCode}
      />
    ));

  const local = useLanguageValue();
  const renderExtendButton = (count: number) => (
    <div className={style.buttonContainer}>
      <button type="button" className={style.buttonExtend} disabled>
        {`+${pluralize(count, productHiddenLabel, local)}`}
      </button>
    </div>
  );

  if (atXXLarge) {
    return (
      <div className={style.grid}>
        {filteredLineItems.length > 4 ? (
          <>
            {createLineItemsList(filteredLineItems.slice(0, 3))}
            {renderExtendButton(filteredLineItems.length - 3)}
          </>
        ) : (
          createLineItemsList(filteredLineItems)
        )}
      </div>
    );
  }

  if (atLarge) {
    return (
      <div className={style.grid}>
        {filteredLineItems.length > 3 ? (
          <>
            {createLineItemsList(filteredLineItems.slice(0, 3))}
            {renderExtendButton(filteredLineItems.length - 3)}
          </>
        ) : (
          createLineItemsList(filteredLineItems)
        )}
      </div>
    );
  }

  if (atMedium) {
    return (
      <div className={style.grid}>
        {filteredLineItems.length > 2 ? (
          <>
            {createLineItemsList(filteredLineItems.slice(0, 2))}
            {renderExtendButton(filteredLineItems.length - 2)}
          </>
        ) : (
          createLineItemsList(filteredLineItems)
        )}
      </div>
    );
  }

  if (atSmall) {
    return (
      <div className={style.grid}>
        {createLineItemsList(filteredLineItems.slice(0, 1))}
        {filteredLineItems.length > 1 && renderExtendButton(filteredLineItems.length - 1)}
      </div>
    );
  }

  return (
    <div className={style.grid}>
      {filteredLineItems.length > 4 ? (
        <>
          {createLineItemsList(filteredLineItems.slice(0, 4))}
          {renderExtendButton(filteredLineItems.length - 4)}
        </>
      ) : (
        createLineItemsList(filteredLineItems)
      )}
    </div>
  );
};
